import { AllocationRuleDto, JourneyResponse } from '@yard/app/api'
import { mapFormDataToAllocationRule } from '@yard/components/AllocationRules/helpers/allocation-rule.mapper'
import { AllocationRuleFormData } from '@yard/components/AllocationRules/models/allocation-rule.model'
import { AllocationRuleStore } from '@yard/stores/allocation-rule.store'
import { CapacitySettingsStore } from '@yard/stores/CapacitySettingsStore'
import { JourneyStore } from '@yard/stores/journey.store'
import { OccupancyStore } from '@yard/stores/OccupancyStore'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import {
  getRuleCapacity,
  RuleCapacity,
} from '@yard/utils/allocation-rule-capacity.util'
import {
  copyAllocationRule,
  generateAllocationRuleId,
  isNewAllocationRule,
} from '@yard/utils/allocation-rules.util'
import { aggregateJourneysByRules } from '@yard/utils/journey.util'
import { action, computed, makeObservable, observable } from 'mobx'

export interface VisitRuleCopySelection {
  rule: AllocationRuleDto
  name: string
}

export interface IBaseAllocationRulesViewStore {
  allocationRules: AllocationRuleDto[]
  isAllocationRulesDrawerOpen: boolean
  hoveredRuleIndex?: number
  editingRuleIndex?: number
  isFormDialogOpen: boolean
  isTemplateDialogOpen: boolean
  isCopyFromVisitDialogOpen: boolean
  editedRulesIds: string[]
  deletedRulesIds: string[]
  hasUnsavedChanges: boolean
  isEditing: boolean
  editingRule: AllocationRuleDto | null
  templateRules: AllocationRuleDto[]
  unallocatedContainers: JourneyResponse[]
  unallocatedContainersCount: number
  toggleFormDialog: (index?: number) => void
  setEditingRuleIndex: (index?: number) => void
  setHoveredRuleIndex: (index?: number) => void
  setAllocationRulesDrawerOpen: (isOpen: boolean) => void
  setAllocationRules: (allocationRules: AllocationRuleDto[]) => void
  toggleTemplateDialog: () => void
  toggleCopyFromVisitDialog: () => void
  copyTemplateRule: (templateRule: AllocationRuleDto) => void
  copyVisitRules: (selections: VisitRuleCopySelection[]) => void
  reorderAllocationRule: (sourceIndex: number, destinationIndex: number) => void
  deleteAllocationRule: (index: number) => void
  submitRules: () => Promise<void>
  resetChanges: () => void
  upsertRuleFromFormData: (formData: AllocationRuleFormData) => void
  upsertRule: (rule: AllocationRuleDto) => void
}

export abstract class BaseAllocationRulesViewStore implements IBaseAllocationRulesViewStore {
  allocationRules: AllocationRuleDto[] = []

  isAllocationRulesDrawerOpen = false

  hoveredRuleIndex?: number
  editingRuleIndex?: number
  isFormDialogOpen = false

  isTemplateDialogOpen = false
  isCopyFromVisitDialogOpen = false
  editedRulesIds: string[] = []
  deletedRulesIds: string[] = []

  constructor(
    readonly allocationRuleStore: AllocationRuleStore,
    readonly occupancyStore: OccupancyStore,
    readonly journeyStore: JourneyStore,
    readonly yardBlockStore: YardBlockStore,
    readonly capacitySettingsStore: CapacitySettingsStore,
  ) {
    makeObservable(this, {
      allocationRules: observable,
      isTemplateDialogOpen: observable,
      isCopyFromVisitDialogOpen: observable,
      isFormDialogOpen: observable,
      hoveredRuleIndex: observable,
      editingRuleIndex: observable,
      editedRulesIds: observable,
      deletedRulesIds: observable,
      isAllocationRulesDrawerOpen: observable,

      orderedAllocationRules: computed,
      hasUnsavedChanges: computed,
      isEditing: computed,
      editingRule: computed,
      templateRules: computed,
      unallocatedContainers: computed,
      unallocatedContainersCount: computed,
      rulesForCapacity: computed,
      yardWideJourneysByRule: computed,
      capacityByRule: computed,
      matchingCountByRule: computed,

      upsertRule: action,
      copyTemplateRule: action,
      copyVisitRules: action,
      toggleTemplateDialog: action,
      toggleCopyFromVisitDialog: action,
      toggleFormDialog: action,
      setAllocationRules: action,
      setAllocationRulesDrawerOpen: action,
      reorderAllocationRule: action,
      deleteAllocationRule: action,
      setEditingRuleIndex: action,
      setHoveredRuleIndex: action,
      resetChanges: action,
    })
  }

  toggleFormDialog(index?: number) {
    this.setEditingRuleIndex(index)
    this.isFormDialogOpen = !this.isFormDialogOpen
  }

  setEditingRuleIndex(index?: number) {
    this.editingRuleIndex = index
  }

  setHoveredRuleIndex(index?: number) {
    this.hoveredRuleIndex = index
  }

  setAllocationRulesDrawerOpen(isOpen: boolean) {
    this.isAllocationRulesDrawerOpen = isOpen
  }

  setAllocationRules(allocationRules: AllocationRuleDto[]) {
    this.allocationRules = []
    const sorted = [...allocationRules].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    sorted.forEach(rule => this.upsertRule(rule))
  }

  upsertRuleFromFormData(formData: AllocationRuleFormData) {
    this.upsertRule({
      ...mapFormDataToAllocationRule(
        formData,
        this.editingRule?.id,
        this.editingRule?.priority ?? this.allocationRules.length,
      ),
    })
  }

  upsertRule(rule: AllocationRuleDto) {
    if (this.editingRuleIndex === undefined) {
      this.allocationRules = [...this.allocationRules, rule]
      return
    }

    this.allocationRules = this.allocationRules.map((existing, index) =>
      index === this.editingRuleIndex ? rule : existing,
    )

    if (
      rule.id &&
      !isNewAllocationRule(rule.id) &&
      !this.editedRulesIds.includes(rule.id)
    ) {
      this.editedRulesIds.push(rule.id)
    }
  }

  toggleTemplateDialog() {
    this.isTemplateDialogOpen = !this.isTemplateDialogOpen
  }

  toggleCopyFromVisitDialog() {
    this.isCopyFromVisitDialogOpen = !this.isCopyFromVisitDialogOpen
  }

  copyTemplateRule(templateRule: AllocationRuleDto) {
    const newRule: AllocationRuleDto = copyAllocationRule(templateRule)
    newRule.id = generateAllocationRuleId()

    this.upsertRule(newRule)
  }

  copyVisitRules(selections: VisitRuleCopySelection[]) {
    selections.forEach(({ rule, name }) => {
      this.copyTemplateRule({
        ...rule,
        name,
        isTemplate: false,
        priority: this.allocationRules.length,
      })
    })
  }

  reorderAllocationRule(sourceIndex: number, destinationIndex: number) {
    if (
      sourceIndex < 0 ||
      destinationIndex < 0 ||
      sourceIndex >= this.allocationRules.length ||
      destinationIndex >= this.allocationRules.length ||
      sourceIndex === destinationIndex
    ) {
      return
    }

    const updated = [...this.allocationRules]
    const [moved] = updated.splice(sourceIndex, 1)
    updated.splice(destinationIndex, 0, moved)

    updated.forEach((rule, index) => (rule.priority = index))
    const idsToAdd = updated
      .map(rule => rule.id)
      .filter(id => id && !isNewAllocationRule(id) && !this.editedRulesIds.includes(id))
    this.editedRulesIds.push(...idsToAdd)

    this.allocationRules = updated
  }

  deleteAllocationRule(index: number) {
    if (index < 0 || index >= this.allocationRules.length) return

    const ruleToDelete = this.allocationRules[index]
    if (ruleToDelete.id && !isNewAllocationRule(ruleToDelete.id)) {
      this.deletedRulesIds.push(ruleToDelete.id)
    }

    this.allocationRules = this.allocationRules.filter((_, i) => i !== index)
  }

  abstract submitRules(): Promise<void>

  resetChanges() {
    this.editingRuleIndex = undefined
    this.isFormDialogOpen = false
    this.isTemplateDialogOpen = false
    this.isCopyFromVisitDialogOpen = false
    this.isAllocationRulesDrawerOpen = false
    this.deletedRulesIds = []
    this.editedRulesIds = []
  }

  /**
   * Merged rule universe: persisted CV + global rules with any in-flight edits from
   * `this.allocationRules` layered on top (same id wins) plus any brand-new rules not yet
   * saved. Rules marked for deletion are dropped. This is what capacity and priority
   * calculations consume so destination/priority edits in the drawer refresh the
   * capacity bar immediately without waiting for save.
   */
  get rulesForCapacity(): AllocationRuleDto[] {
    const persisted = [
      ...this.allocationRuleStore.carrierVisitRules,
      ...this.allocationRuleStore.globalRules,
    ]
    const localById = new Map(this.allocationRules.map(r => [r.id, r]))
    const deleted = new Set(this.deletedRulesIds)
    const persistedIds = new Set(persisted.map(r => r.id))

    const persistedWithEdits = persisted
      .filter(r => !deleted.has(r.id))
      .map(r => localById.get(r.id) ?? r)

    const localOnly = this.allocationRules.filter(r => !persistedIds.has(r.id))

    return [...persistedWithEdits, ...localOnly]
  }

  /**
   * Yard-wide per-rule bucketing of journeys, single source of truth for both matching
   * counts and capacity. The same journey + rule universe is used regardless of which
   * subclass (global or CV) is live, so capacity (a physical-space measure) is always
   * identical across views and CV-scoped matching is a clean subset of the yard-wide
   * bucket (via `matchingCountByRule` / `unallocatedContainers` overrides below).
   */
  get yardWideJourneysByRule(): Record<string, JourneyResponse[]> {
    return aggregateJourneysByRules(this.journeyStore.items, this.rulesForCapacity)
  }

  /**
   * Capacity is always yard-wide: occupied/reserved TEU come from positions in the
   * destination, planned TEU from yard-wide winning journeys. Iterates every rule (CV +
   * global) so the CV drawer and the global view can read identical numbers for the same
   * rule id.
   */
  get capacityByRule(): Record<string, RuleCapacity> {
    const journeysByRuleId = this.yardWideJourneysByRule
    const warningThreshold = this.capacitySettingsStore.allocationRuleWarningThreshold

    return this.rulesForCapacity.reduce<Record<string, RuleCapacity>>((acc, rule) => {
      acc[rule.id] = getRuleCapacity(
        rule,
        {
          yardBlocks: this.yardBlockStore.items,
          occupanciesByBlock: this.occupancyStore.occupanciesByBlock,
          journeysByRuleId,
        },
        warningThreshold,
      )
      return acc
    }, {})
  }

  /**
   * Matching count per rule: how many journeys currently win this rule. Default is
   * yard-wide (used by the global rules view). The CV drawer view overrides this to
   * scope the count to `selectedCarrierVisitJourneys`.
   */
  get matchingCountByRule(): Record<string, number> {
    const buckets = this.yardWideJourneysByRule
    const result: Record<string, number> = {}
    Object.keys(buckets).forEach(ruleId => {
      if (ruleId === 'unassigned') return
      result[ruleId] = buckets[ruleId].length
    })
    return result
  }

  get isEditing(): boolean {
    return this.editingRuleIndex !== undefined && this.editingRuleIndex !== null
  }

  get editingRule(): AllocationRuleDto | null {
    if (!this.isEditing) return null
    return this.allocationRules[this.editingRuleIndex!]
  }

  get templateRules(): AllocationRuleDto[] {
    return this.allocationRuleStore.templateRules
  }

  get unallocatedContainers(): JourneyResponse[] {
    return this.yardWideJourneysByRule['unassigned'] ?? []
  }

  get unallocatedContainersCount(): number {
    return this.unallocatedContainers.length
  }

  get orderedAllocationRules(): AllocationRuleDto[] {
    return this.allocationRules.slice().sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
  }

  get hasUnsavedChanges(): boolean {
    const hasUnsavedChanges =
      this.deletedRulesIds.length > 0 ||
      this.editedRulesIds.length > 0 ||
      this.allocationRules.some(rule => isNewAllocationRule(rule.id))

    return hasUnsavedChanges
  }
}
